import cn from 'classnames';
import { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { Button } from '@/shared/ui';

import { reportsModel, useCheckReportSignature } from '@/entities/reports';
import { toothModel } from '@/entities/tooth';

import { useToothNumberEditing } from '../../hooks/useToothNumberEditing';
import {
  buildDataByAssetAndDetection,
  calculateSupernumeraryIndices,
  createUpdateRequests,
  extractChangesFromResult,
  groupChangesByAsset,
  groupToothAndChangesByAsset,
  splitPendingChanges,
} from '../../lib';
import * as toothNumberEditModel from '../../model';

import styles from './TeethEditActions.module.scss';

type TeethEditActionsProps = {
  className?: string;
  testID?: string;
  isHiding?: boolean;
};

export const TeethEditActions: FC<TeethEditActionsProps> = (props) => {
  const { className, testID, isHiding } = props;

  const dispatch = useAppDispatch();

  const { checkReportSignature } = useCheckReportSignature();
  const { handleCloseEditMode } = useToothNumberEditing();

  const pendingChanges = useAppSelector(
    toothNumberEditModel.selectors.selectPendingChanges,
  );

  const teeth = useAppSelector(toothModel.selectors.selectForCurrentReport);
  const reportID = useAppSelector(reportsModel.selectors.selectCurrentReportID);
  const reportType = useAppSelector(
    reportsModel.selectors.selectCurrentReportType,
  );

  const handleCancel = useCallback(() => {
    dispatch(toothNumberEditModel.actions.resetState());
    handleCloseEditMode();
  }, [handleCloseEditMode]);

  const handleConfirm = useCallback(async () => {
    const { removes, changes } = splitPendingChanges(pendingChanges);

    const changesByAssetID = groupChangesByAsset(changes, teeth);
    const dataByAssetIDAndDetection = buildDataByAssetAndDetection(
      teeth,
      changesByAssetID,
    );
    const toothAndChangesForAsset = groupToothAndChangesByAsset(
      dataByAssetIDAndDetection,
    );

    const toothAndChangesWithIndices = calculateSupernumeraryIndices(
      toothAndChangesForAsset,
    );

    const updatedChanges = extractChangesFromResult(toothAndChangesWithIndices);

    checkReportSignature({
      onSignatureChecked: async () => {
        try {
          if (!reportID) {
            return;
          }

          const updates = createUpdateRequests(
            removes,
            updatedChanges,
            reportType,
          );

          await dispatch(
            reportsModel.thunks.updateTeethState({
              ReportID: reportID,
              Updates: updates,
            }),
          );
        } finally {
          dispatch(toothNumberEditModel.actions.resetState());
          handleCloseEditMode();
        }
      },
    });
  }, [
    pendingChanges,
    teeth,
    reportID,
    reportType,
    checkReportSignature,
    handleCloseEditMode,
  ]);

  return (
    <div
      data-testid={testID}
      data-component="teeth-edit-actions"
      className={cn(styles.container, className, isHiding && styles.hiding)}
    >
      <Button
        variant="secondary"
        size="small"
        onClick={handleCancel}
        className={cn(styles.button, styles.cancelButton)}
      >
        <FormattedMessage id="global.cancel" defaultMessage="Cancel" />
      </Button>
      <Button
        variant="primary"
        size="small"
        onClick={handleConfirm}
        disabled={pendingChanges.length === 0}
        className={cn(styles.button, styles.confirmButton)}
      >
        <FormattedMessage id="global.confirm" defaultMessage="Confirm" />
      </Button>
    </div>
  );
};
